import { useEffect, useRef } from "react";
import { gregorianToJalali, jalaliToGregorian } from "../../utils/jalali";

const PLUGIN_SCRIPT = "/assets/js/persianDatepicker.min.js";
const PLUGIN_CSS = "/assets/css/persianDatepicker-default.css";

declare global {
  interface JQuery {
    persianDatepicker(options: object): JQuery;
  }
}

type PickerJQuery = JQueryStatic & { fn: { persianDatepicker?: unknown } };

let loading: Promise<PickerJQuery> | null = null;

/**
 * The template's persianDatepicker is a jQuery plugin (the template loads jQuery 3.7.1 from a CDN);
 * jQuery comes from npm here and is only loaded on admin pages that have a date field.
 */
function loadPicker(): Promise<PickerJQuery> {
  loading ??= import("jquery").then(
    ({ default: jQuery }) =>
      new Promise<PickerJQuery>((resolve, reject) => {
        const w = window as unknown as { jQuery?: JQueryStatic };
        w.jQuery = jQuery;
        if (!document.querySelector(`link[href="${PLUGIN_CSS}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = PLUGIN_CSS;
          document.head.appendChild(link);
        }
        const script = document.createElement("script");
        script.src = PLUGIN_SCRIPT;
        script.onload = () => resolve(jQuery as PickerJQuery);
        script.onerror = () => {
          loading = null;
          reject(new Error(`Could not load ${PLUGIN_SCRIPT}`));
        };
        document.body.appendChild(script);
      })
  );
  return loading;
}

interface JalaliDateInputProps {
  id: string;
  /** Gregorian "YYYY-MM-DD" (what the API stores) or "" */
  value: string;
  /** called with the Gregorian date, or "" when the field is empty / not a valid Shamsi date */
  onChange: (gregorian: string) => void;
  required?: boolean;
  placeholder?: string;
}

/**
 * Shamsi date field with the template's persian date picker. Shows and accepts "1403/05/12";
 * the form value (and so the request to the server) stays Gregorian.
 */
export default function JalaliDateInput({ id, value, onChange, required, placeholder = "۱۴۰۳/۰۵/۱۲" }: JalaliDateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // the plugin writes into the input itself, so the input is not controlled by React
  // `tidy` rewrites a valid date as 1403/05/12 (the plugin writes 1403/5/12); not while typing
  const commit = (tidy = false) => {
    const input = inputRef.current;
    if (!input) return;
    const text = input.value.trim();
    const gregorian = jalaliToGregorian(text);
    input.setCustomValidity(text && !gregorian ? "invalid date" : "");
    if (tidy && gregorian) input.value = gregorianToJalali(gregorian);
    onChangeRef.current(gregorian);
  };

  // a new value from outside (e.g. the item finished loading)
  useEffect(() => {
    const input = inputRef.current;
    if (input && jalaliToGregorian(input.value) !== value) {
      input.value = gregorianToJalali(value);
      input.setCustomValidity("");
    }
  }, [value]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    let cancelled = false;

    loadPicker()
      .then(($) => {
        if (cancelled || !$.fn.persianDatepicker) return;
        $(input).persianDatepicker({
          formatDate: "YYYY/MM/DD",
          persianNumbers: true,
          // a valid value already in the input is taken as the selected date
          selectedBefore: !!input.value,
          onSelect: () => commit(true),
        });
        commit(true);
      })
      .catch((err) => console.error(err));

    return () => {
      cancelled = true;
      // the plugin appends its calendar to <body> and has no destroy()
      const calendarId = input.getAttribute("pdp-id");
      if (calendarId) document.getElementById(calendarId)?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="input-group">
      <div className="input-group-text text-muted">
        <i className="ri-calendar-line"></i>
      </div>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="form-control"
        dir="ltr"
        autoComplete="off"
        inputMode="numeric"
        placeholder={placeholder}
        defaultValue={gregorianToJalali(value)}
        onChange={() => commit()}
        onBlur={() => commit(true)}
        required={required}
      />
    </div>
  );
}
